import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams, Link } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowLeft,
  Film,
  Loader2,
  Volume2,
  Lightbulb,
  BookOpen,
  FileText,
  ListOrdered,
  Layers,
  Wand2,
  ChevronDown,
  CheckCircle2,
  AlertCircle,
  Play,
  RotateCcw,
  Clapperboard,
  Captions,
} from "lucide-react";
import type { VideoExpressCampaign, VideoExpressScript, VideoExpressRender } from "@shared/schema";
import { ScriptRenderControls } from "@/components/script-render-controls";
import { VideoExpressClipEditor } from "@/components/video-express-clip-editor";
import { isActive, deriveClipStepState, deriveRenderStepState, deriveCaptionStepState } from "@/lib/video-express-pipeline";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

// Mirror of the backend `allSceneClipsReady` (server/routes.ts): a script's
// final video can only be stitched once EVERY canonical scene (extracted panel,
// or prompted scene for legacy scripts) has a rendered clip. Keep this in
// lockstep with the backend gate to avoid producing incomplete stitched videos.
function allClipsReady(s: any): boolean {
  const vs = (s?.sceneVideos as any[]) || [];
  if (vs.length === 0) return false;
  if (!vs.every((v: any) => v?.videoUrl)) return false;
  const panels = (s?.panelImages as any[]) || [];
  const canonical = (panels.length > 0
    ? panels
    : ((s?.scenePrompts as any[]) || []).filter((p: any) => p?.prompt)
  ).map((x: any) => Number(x.scene_number));
  if (canonical.length === 0) return false;
  return canonical.every((n: number) => vs.some((v: any) => Number(v.scene_number) === n && v?.videoUrl));
}

function parseFullScript(s: any): any {
  try {
    return typeof s?.fullScript === "string" ? JSON.parse(s.fullScript) : (s?.fullScript || {});
  } catch {
    return {};
  }
}

// ---------------------------------------------------------------------------
// Full-pipeline walkthrough (collapsed by default).
// The edit page leads with the interactive scene-clip grids (Visual / Motion /
// Re-render per clip) plus the final-render controls — that's what editing is
// for. The read-only production steps behind a finished Video Express Max
// video (Creative Strategy -> Concept -> Script & Narration -> Beat Map ->
// Storyboards -> Scene Prompts -> Voiceover) stay available behind a
// "View full pipeline" toggle below the clips. All data already rides on the
// campaign + scripts queries; nothing here hits new endpoints.
// ---------------------------------------------------------------------------

function StepStatusBadge({ status }: { status?: string }) {
  if (!status || status === "idle") return null;
  const s = String(status).toLowerCase();
  if (["generating", "queued", "processing", "pending", "running"].includes(s)) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 text-amber-600 text-[10px] font-semibold px-2 py-0.5 shrink-0">
        <Loader2 className="h-3 w-3 animate-spin" /> Generating
      </span>
    );
  }
  if (["ready", "completed", "complete", "done"].includes(s)) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 text-emerald-600 text-[10px] font-semibold px-2 py-0.5 shrink-0">
        <CheckCircle2 className="h-3 w-3" /> Ready
      </span>
    );
  }
  if (["failed", "error"].includes(s)) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-500/10 text-red-600 text-[10px] font-semibold px-2 py-0.5 shrink-0">
        <AlertCircle className="h-3 w-3" /> Failed
      </span>
    );
  }
  return null;
}

function PipelineStep({
  icon: Icon,
  label,
  sub,
  status,
  defaultOpen,
  children,
  testid,
}: {
  icon: any;
  label: string;
  sub?: string;
  status?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
  testid?: string;
}) {
  return (
    <details
      open={defaultOpen}
      className="group/step rounded-lg border border-border/40 bg-muted/[0.02] overflow-hidden"
      data-testid={testid}
    >
      <summary className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none hover:bg-muted/30 transition-colors list-none [&::-webkit-details-marker]:hidden">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-violet-500/10 text-violet-600 shrink-0">
          <Icon className="h-3.5 w-3.5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold uppercase tracking-wider text-foreground/80">{label}</p>
          {sub ? <p className="text-xs text-muted-foreground truncate">{sub}</p> : null}
        </div>
        <StepStatusBadge status={status} />
        <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-open/step:rotate-180 shrink-0" />
      </summary>
      <div className="px-4 pb-4 pt-3 border-t border-border/30 space-y-3">{children}</div>
    </details>
  );
}

function StratField({ label, value }: { label: string; value?: any }) {
  if (!value || typeof value !== "string") return null;
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-0.5">{label}</p>
      <p className="text-sm leading-relaxed text-foreground/90 whitespace-pre-wrap">{value}</p>
    </div>
  );
}

function CreativeStrategyContent({ strategy }: { strategy: any }) {
  const brief = (strategy && (strategy.video_creative_brief || strategy)) || {};
  const spine = brief.creative_spine || {};
  const angle = brief.angle_context || {};
  const persona = brief.persona_context || {};
  const fmt = brief.scene_format || {};
  const fmtParts = [
    fmt.scene_count ? `${fmt.scene_count} scenes` : null,
    fmt.runtime_seconds ? `${fmt.runtime_seconds}s runtime` : null,
    fmt.scene_duration_seconds ? `${fmt.scene_duration_seconds}s per scene` : null,
  ].filter(Boolean);
  return (
    <>
      <StratField label="Main message" value={spine.main_message} />
      <StratField label="Core tension" value={spine.core_tension} />
      <StratField label="Story spine" value={spine.story_spine} />
      <StratField label="Emotional movement" value={spine.emotional_movement} />
      <StratField label="Selected angle" value={angle.selected_angle} />
      <StratField label="Main marketing idea" value={angle.main_marketing_idea} />
      <StratField label="Persona" value={persona.persona_name} />
      <StratField label="Persona summary" value={persona.persona_summary} />
      <StratField label="Main pain point" value={persona.main_pain_point} />
      {fmtParts.length > 0 ? (
        <p className="text-xs text-muted-foreground">{fmtParts.join(" · ")}</p>
      ) : null}
      <details className="group/raw">
        <summary className="text-xs text-violet-600 cursor-pointer select-none w-fit">View full creative brief</summary>
        <pre className="mt-2 text-[10px] leading-relaxed bg-muted/40 rounded-md p-3 max-h-80 overflow-auto whitespace-pre-wrap">
          {JSON.stringify(strategy, null, 2)}
        </pre>
      </details>
    </>
  );
}

function ScriptContent({ fs, script }: { fs: any; script: any }) {
  const concept = fs?.concept || {};
  const scenes = Array.isArray(fs?.scenes) ? fs.scenes : [];
  const narration =
    scenes.map((s: any) => s?.narration).filter(Boolean).join("\n\n") || script?.body || "";
  return (
    <>
      <StratField label="Concept" value={concept.concept_title || fs?.script_title} />
      <StratField label="Summary" value={concept.concept_summary} />
      <StratField label="Story arc" value={concept.story_arc_summary} />
      <StratField label="Narrator direction" value={concept.narrator_direction} />
      {narration ? (
        <div>
          <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-0.5">Narration</p>
          <p className="text-sm leading-relaxed text-foreground/90 whitespace-pre-wrap">{narration}</p>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">No narration yet.</p>
      )}
    </>
  );
}

function BeatMapContent({ fs }: { fs: any }) {
  const beats = Array.isArray(fs?.beats) ? fs.beats : [];
  if (beats.length === 0) return <p className="text-xs text-muted-foreground">No beat map yet.</p>;
  return (
    <ol className="space-y-2">
      {beats.map((b: any, i: number) => {
        const ts = b?.timestamp || {};
        const range =
          ts.start_time && ts.end_time
            ? `${ts.start_time}–${ts.end_time}`
            : b?.time_range || `Scene ${b?.scene_number ?? i + 1}`;
        const meta = [b?.story_function, b?.emotional_tone].filter(Boolean).join(" · ");
        return (
          <li key={i} className="rounded-md bg-muted/30 p-2.5" data-testid={`beat-${i}`}>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] font-mono font-semibold text-violet-600">{range}</span>
              {meta ? <span className="text-[10px] text-muted-foreground truncate">{meta}</span> : null}
            </div>
            {b?.voiceover ? <p className="text-xs text-foreground/90 mb-1">“{b.voiceover}”</p> : null}
            {b?.primary_action ? (
              <p className="text-[11px] text-muted-foreground leading-snug">{b.primary_action}</p>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

function combineStoryboardStatus(s: any): string | undefined {
  const statuses = [s?.storyboardStatus, s?.storyboardStatus2, s?.storyboardStatus3].filter(Boolean) as string[];
  if (statuses.some((x) => x === "generating")) return "generating";
  const urls = [s?.storyboardImageUrl, s?.storyboardImageUrl2, s?.storyboardImageUrl3].filter(Boolean);
  if (urls.length > 0) return "ready";
  if (statuses.some((x) => x === "failed")) return "failed";
  return undefined;
}

function StoryboardsContent({ script }: { script: any }) {
  const urls = [script?.storyboardImageUrl, script?.storyboardImageUrl2, script?.storyboardImageUrl3].filter(
    Boolean,
  ) as string[];
  if (urls.length === 0) return <p className="text-xs text-muted-foreground">No storyboard frames yet.</p>;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {urls.map((u, i) => (
        <a
          key={i}
          href={u}
          target="_blank"
          rel="noreferrer"
          className="block rounded-md overflow-hidden border border-border/40 hover:border-violet-400/60 transition-colors"
          data-testid={`storyboard-${script.id}-${i}`}
        >
          <img src={u} alt={`Storyboard ${i + 1}`} className="w-full h-auto block" loading="lazy" />
        </a>
      ))}
    </div>
  );
}

function ScenePromptsContent({ script }: { script: any }) {
  const prompts = Array.isArray(script?.scenePrompts) ? script.scenePrompts : [];
  if (prompts.length === 0) return <p className="text-xs text-muted-foreground">No scene prompts yet.</p>;
  return (
    <div className="space-y-2 max-h-96 overflow-auto pr-1">
      {prompts.map((p: any, i: number) => (
        <div key={i} className="rounded-md bg-muted/30 p-2.5" data-testid={`scene-prompt-${script.id}-${i}`}>
          <p className="text-[10px] font-semibold text-violet-600 mb-1">Scene {p?.scene_number ?? i + 1}</p>
          <p className="text-[11px] text-foreground/80 leading-snug whitespace-pre-wrap">{p?.prompt || ""}</p>
          {p?.error ? <p className="text-[11px] text-red-600 mt-1">{p.error}</p> : null}
        </div>
      ))}
    </div>
  );
}

function VoiceoverContent({ script }: { script: any }) {
  const voStatus = script?.voiceOverStatus as string | undefined;
  const voUrl = script?.voiceOverUrl as string | undefined;
  const voError = script?.voiceOverError as string | undefined;
  if (voStatus === "generating") {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid={`status-voiceover-${script.id}`}>
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating voiceover…
      </div>
    );
  }
  if (voStatus === "ready" && voUrl) {
    return <audio controls src={voUrl} className="w-full h-9" data-testid={`audio-voiceover-${script.id}`} />;
  }
  if (voStatus === "failed") {
    return (
      <p className="text-xs text-red-600" data-testid={`text-voiceover-error-${script.id}`}>
        {voError || "Voiceover failed"}
      </p>
    );
  }
  return <p className="text-xs text-muted-foreground">No voiceover yet.</p>;
}

/** Small inline "Retry" action for a failed pipeline step. */
function RetryButton({ onClick, disabled, testid, label = "Retry" }: {
  onClick: () => void; disabled?: boolean; testid: string; label?: string;
}) {
  return (
    <Button
      variant="outline"
      size="sm"
      className="rounded-full h-7 px-2.5 text-xs w-fit"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
    >
      <RotateCcw className="h-3 w-3 mr-1" /> {label}
    </Button>
  );
}

export default function VideoExpressCampaignEditPage() {
  const { brandId, videoExpressCampaignId } = useParams<{ brandId: string; videoExpressCampaignId: string }>();
  const { toast } = useToast();
  // The read-only production-pipeline walkthrough is hidden by default — the
  // page leads with the editable clips. Toggled via "View full pipeline".
  const [showPipeline, setShowPipeline] = useState(false);

  const { data: campaign, isLoading: campaignLoading } = useQuery<VideoExpressCampaign>({
    queryKey: ["/api/video-express-campaigns", videoExpressCampaignId],
    queryFn: async () => {
      const res = await fetch(`/api/video-express-campaigns/${videoExpressCampaignId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch campaign");
      return res.json();
    },
    enabled: !!videoExpressCampaignId,
    refetchInterval: (q) => (isActive((q.state.data as any)?.autopilotStatus) ? 4000 : false),
  });

  const { data: scripts, isLoading: scriptsLoading } = useQuery<VideoExpressScript[]>({
    queryKey: ["/api/video-express-campaigns", videoExpressCampaignId, "scripts"],
    queryFn: async () => {
      const res = await fetch(`/api/video-express-campaigns/${videoExpressCampaignId}/scripts`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch scripts");
      return res.json();
    },
    enabled: !!videoExpressCampaignId,
    // Poll while the (server-side, fire-and-forget) pipeline is still producing
    // scenes/clips so freshly generated/edited assets surface here without a
    // manual refresh.
    refetchInterval: (q) => {
      const data = (q.state.data as VideoExpressScript[] | undefined) || [];
      if (data.length === 0) {
        return isActive((campaign as any)?.autopilotStatus) ? 5000 : false;
      }
      if (data.some((s: any) => s.storyboardStatus === "generating" || s.storyboardStatus2 === "generating" || s.storyboardStatus3 === "generating")) return 3000;
      if (data.some((s: any) => parseFullScript(s)?.beatsStatus === "generating")) return 3000;
      if (data.some((s: any) => s.panelStatus === "generating")) return 3000;
      if (data.some((s: any) =>
        Array.isArray(s.panelImages) &&
        (s.panelImages as any[]).some((p: any) => p?.edit_status === "pending")
      )) return 3000;
      if (data.some((s: any) =>
        Array.isArray(s.panelImages) &&
        (s.panelImages as any[]).some((p: any) => p?.upscale_status === "queued" || p?.upscale_status === "processing")
      )) return 3000;
      const anyPendingVideo = data.some((s: any) => {
        const vids = s.sceneVideos as any[] | null;
        return Array.isArray(vids) && vids.some((v: any) => v && !v.videoUrl && !v.error && v.status !== "paused");
      });
      if (anyPendingVideo) return 5000;
      return false;
    },
  });

  // Latest final render per script — powers the Final Render + Captions rows in
  // the pipeline walkthrough so their persisted errors are visible on this page.
  const { data: rendersByScript } = useQuery<Record<string, VideoExpressRender>>({
    queryKey: ["/api/video-express-campaigns", videoExpressCampaignId, "renders", "latest"],
    queryFn: async () => {
      const res = await fetch(`/api/video-express-campaigns/${videoExpressCampaignId}/renders/latest`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load renders");
      return res.json();
    },
    enabled: !!videoExpressCampaignId,
    refetchInterval: (q) => {
      const map = (q.state.data as Record<string, any>) || {};
      const busy = Object.values(map).some((r: any) =>
        r && (r.status === "queued" || r.status === "rendering" || r.captionedVideoStatus === "generating"));
      return busy ? 4000 : false;
    },
  });

  const backHref = `/brands/${brandId}/video-express-campaigns`;

  const invalidatePipeline = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/video-express-campaigns", videoExpressCampaignId] });
    queryClient.invalidateQueries({ queryKey: ["/api/video-express-campaigns", videoExpressCampaignId, "scripts"] });
    queryClient.invalidateQueries({ queryKey: ["/api/video-express-campaigns", videoExpressCampaignId, "renders", "latest"] });
  };

  // Resume an interrupted/failed autopilot run — the explicit user action that
  // continues the pipeline from the first unfinished step (nothing restarts on
  // its own after a failure or server restart).
  const resumeMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/video-express-campaigns/${videoExpressCampaignId}/resume-autopilot`);
    },
    onSuccess: () => {
      toast({ title: "Resuming run", description: "Continuing from the first unfinished step." });
      invalidatePipeline();
    },
    onError: (e: any) => {
      toast({ title: "Couldn't resume the run", description: e?.message, variant: "destructive" });
    },
  });

  // One shared mutation drives every per-step Retry button (each passes its own
  // route). The routes are the same manual generation endpoints the pipeline
  // page uses; the server 409s them while autopilot owns the campaign.
  const stepRetry = useMutation({
    mutationFn: async ({ path, body }: { path: string; body?: Record<string, unknown> }) => {
      await apiRequest("POST", path, body);
    },
    onSuccess: () => {
      toast({ title: "Retrying step", description: "Regeneration started — progress appears below." });
      invalidatePipeline();
    },
    onError: (e: any) => {
      toast({ title: "Couldn't retry this step", description: e?.message, variant: "destructive" });
    },
  });

  if (campaignLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center space-y-4">
        <p className="text-sm text-muted-foreground" data-testid="text-campaign-not-found">Campaign not found.</p>
        <Link href={backHref}>
          <Button variant="outline" size="sm" className="rounded-full" data-testid="button-back-to-campaigns">
            <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Back to Video Express Max
          </Button>
        </Link>
      </div>
    );
  }

  const orderedScripts = scripts || [];
  const strat = (campaign as any).creativeStrategy;
  const stratStatus = (campaign as any).creativeStrategyStatus as string | undefined;
  const stratError = (campaign as any).creativeStrategyError as string | undefined;
  const conceptError = (campaign as any).conceptError as string | undefined;
  const firstFs = orderedScripts.length > 0 ? parseFullScript(orderedScripts[0]) : {};
  const conceptText: string =
    (campaign.concept as any) || firstFs?.concept?.concept_summary || firstFs?.concept_summary || "";
  const conceptPremise: string = firstFs?.concept?.concept_premise || "";
  const showStrategy = !!strat || (!!stratStatus && stratStatus !== "idle");
  const autopilotStatus = (campaign as any).autopilotStatus as string | null | undefined;
  // Retries are only blocked while autopilot actually OWNS the campaign — an
  // interrupted/failed/partial/cancelled run leaves every manual route open.
  const autopilotRunning = autopilotStatus === "running";
  const retryDisabled = autopilotRunning || stepRetry.isPending;
  const campaignApi = `/api/video-express-campaigns/${videoExpressCampaignId}`;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6" data-testid="page-video-express-edit">
      <div className="min-w-0">
        <Link href={backHref}>
          <button
            type="button"
            className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors mb-2"
            data-testid="button-back-to-campaigns"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Video Express Max
          </button>
        </Link>
        <h1 className="text-xl font-bold truncate flex items-center gap-2" data-testid="text-campaign-name">
          <Film className="h-5 w-5 text-violet-500 shrink-0" />
          {campaign.name || "Untitled campaign"}
        </h1>
        {campaign.videoStyle && (
          <p className="text-xs text-muted-foreground mt-1" data-testid="text-campaign-style">{campaign.videoStyle}</p>
        )}
        <p className="text-[11px] text-muted-foreground/70 mt-1">Edit your video clips — hover a clip to change its visuals or motion, or re-render it.</p>
      </div>

      {(autopilotStatus === "interrupted" || autopilotStatus === "failed") && (
        <div
          className={`rounded-lg border px-4 py-3 ${autopilotStatus === "interrupted"
            ? "border-amber-500/30 bg-amber-500/5"
            : "border-destructive/30 bg-destructive/5"}`}
          data-testid={autopilotStatus === "interrupted" ? "banner-run-interrupted" : "banner-run-failed"}
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1 space-y-0.5">
              <p className={`text-sm font-semibold ${autopilotStatus === "interrupted"
                ? "text-amber-700 dark:text-amber-400" : "text-destructive"}`}>
                {autopilotStatus === "interrupted" ? "This run was interrupted" : "This run failed"}
              </p>
              <p className="text-xs text-muted-foreground" data-testid="text-run-halt-reason">
                {(campaign as any).autopilotError
                  || (autopilotStatus === "interrupted"
                    ? "A server restart stopped the pipeline. Finished steps are saved — nothing restarts on its own."
                    : "The pipeline stopped. Fix the failed step below or resume the run.")}
              </p>
            </div>
            <Button
              size="sm"
              className="rounded-full shrink-0"
              onClick={() => resumeMutation.mutate()}
              disabled={resumeMutation.isPending}
              data-testid="button-resume-autopilot"
            >
              <Play className="h-3.5 w-3.5 mr-1" /> Resume run
            </Button>
          </div>
        </div>
      )}

      {scriptsLoading && orderedScripts.length === 0 ? (
        <div className="space-y-3">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : orderedScripts.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 flex flex-col items-center text-center gap-3">
            <div className="w-12 h-12 rounded-full bg-muted/50 flex items-center justify-center">
              <Loader2 className="h-5 w-5 text-muted-foreground/60 animate-spin" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium" data-testid="text-still-generating">Your video is still generating</p>
              <p className="text-xs text-muted-foreground max-w-sm">
                Your clips appear here as soon as they're rendered. Track live progress on the Video Express Max list.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8" data-testid="list-scripts">
          {orderedScripts.map((script, idx) => (
            <div
              key={script.id}
              className={`space-y-3 ${idx > 0 ? "border-t border-border/40 pt-8" : ""}`}
              data-testid={`script-section-${script.id}`}
            >
              {orderedScripts.length > 1 ? (
                <p className="text-xs font-semibold text-muted-foreground">Video {idx + 1}</p>
              ) : null}

              {/* Individual clips — each with an Edit button (visual / motion / re-render product) */}
              <VideoExpressClipEditor script={script} campaignId={videoExpressCampaignId} />

              {/* One small control to rebuild the full combined video (unchanged behavior) */}
              <ScriptRenderControls scriptId={script.id} hasScenes={allClipsReady(script)} variant="video-express" />
            </div>
          ))}
        </div>
      )}

      {/* Full production pipeline — read-only walkthrough, collapsed by default. */}
      <div className="border-t border-border/30 pt-4">
        <button
          type="button"
          onClick={() => setShowPipeline((v) => !v)}
          className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
          data-testid="button-toggle-pipeline"
        >
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showPipeline ? "rotate-180" : ""}`} />
          {showPipeline ? "Hide full pipeline" : "View full pipeline"}
        </button>

        {showPipeline && (
          <div className="mt-4 space-y-6" data-testid="pipeline-walkthrough">
            <p className="text-[11px] text-muted-foreground/70">Every step that built this video.</p>

            {/* Campaign-level pipeline steps (run once, before the per-video back half). */}
            {(showStrategy || conceptText) && (
              <div className="space-y-3" data-testid="pipeline-campaign-steps">
                {showStrategy && (
                  <PipelineStep icon={Lightbulb} label="Creative Strategy" status={stratStatus} testid="step-strategy">
                    {strat ? (
                      <CreativeStrategyContent strategy={strat} />
                    ) : stratStatus === "failed" ? (
                      <p className="text-xs text-muted-foreground">The creative brief was not generated.</p>
                    ) : (
                      <p className="text-xs text-muted-foreground">Working on the creative brief…</p>
                    )}
                    {stratError ? <p className="text-xs text-red-600" data-testid="text-strategy-error">{stratError}</p> : null}
                    {stratStatus === "failed" ? (
                      <RetryButton
                        testid="button-retry-strategy"
                        disabled={retryDisabled}
                        onClick={() => stepRetry.mutate({ path: `${campaignApi}/generate-strategy` })}
                      />
                    ) : null}
                  </PipelineStep>
                )}
                {(conceptText || conceptError) && (
                  <PipelineStep
                    icon={BookOpen}
                    label="Concept"
                    status={conceptError && !conceptText ? "failed" : undefined}
                    defaultOpen
                    testid="step-concept"
                  >
                    {conceptText ? (
                      <p className="text-sm leading-relaxed text-foreground/90 whitespace-pre-wrap">{conceptText}</p>
                    ) : null}
                    {conceptPremise && conceptPremise !== conceptText ? (
                      <StratField label="Premise" value={conceptPremise} />
                    ) : null}
                    {conceptError ? <p className="text-xs text-red-600" data-testid="text-concept-error">{conceptError}</p> : null}
                    {conceptError ? (
                      <RetryButton
                        testid="button-retry-concepts"
                        disabled={retryDisabled}
                        onClick={() => stepRetry.mutate({ path: `${campaignApi}/generate-script-plans` })}
                      />
                    ) : null}
                  </PipelineStep>
                )}
              </div>
            )}

            {orderedScripts.length === 0 ? (
              <p className="text-xs text-muted-foreground" data-testid="text-pipeline-empty">
                Per-video steps appear here once generation gets underway.
              </p>
            ) : (
              orderedScripts.map((script, idx) => {
                const fs = parseFullScript(script);
                const hasNarration = (Array.isArray(fs?.scenes) && fs.scenes.some((s: any) => s?.narration)) || !!(script as any).body;
                const voStatus = (script as any).voiceOverStatus as string | undefined;
                const voError = (script as any).voiceOverError as string | undefined;
                const scriptFailed = !hasNarration && voStatus === "failed";
                const beatsFailed = fs?.beatsStatus === "failed";
                const failedSlots: Array<{ slot: number; error?: string }> = [
                  { slot: 1, status: (script as any).storyboardStatus, error: (script as any).storyboardError },
                  { slot: 2, status: (script as any).storyboardStatus2, error: (script as any).storyboardError2 },
                  { slot: 3, status: (script as any).storyboardStatus3, error: (script as any).storyboardError3 },
                ].filter((x: any) => x.status === "failed");
                const spStatus = (script as any).scenePromptStatus as string | undefined;
                const spError = (script as any).scenePromptError as string | undefined;
                return (
                  <div
                    key={script.id}
                    className={`space-y-3 ${idx > 0 ? "border-t border-border/40 pt-6" : ""}`}
                    data-testid={`pipeline-script-section-${script.id}`}
                  >
                    {orderedScripts.length > 1 ? (
                      <p className="text-xs font-semibold text-muted-foreground">Video {idx + 1}</p>
                    ) : null}

                    <PipelineStep
                      icon={FileText}
                      label="Script & Narration"
                      status={hasNarration ? "ready" : scriptFailed ? "failed" : undefined}
                      testid={`step-script-${script.id}`}
                    >
                      <ScriptContent fs={fs} script={script} />
                      {scriptFailed ? (
                        <>
                          <p className="text-xs text-red-600" data-testid={`text-script-error-${script.id}`}>
                            {voError || "Script generation failed."}
                          </p>
                          <RetryButton
                            testid={`button-retry-script-${script.id}`}
                            disabled={retryDisabled}
                            onClick={() => stepRetry.mutate({ path: `${campaignApi}/generate-scripts`, body: { replace: false } })}
                          />
                        </>
                      ) : null}
                    </PipelineStep>

                    <PipelineStep icon={ListOrdered} label="Beat Map" status={fs?.beatsStatus} testid={`step-beats-${script.id}`}>
                      <BeatMapContent fs={fs} />
                      {beatsFailed ? (
                        <>
                          <p className="text-xs text-red-600" data-testid={`text-beats-error-${script.id}`}>
                            {fs?.beatsError || "Beat map generation failed."}
                          </p>
                          <RetryButton
                            testid={`button-retry-beats-${script.id}`}
                            disabled={retryDisabled}
                            onClick={() => stepRetry.mutate({ path: `${campaignApi}/generate-beat-maps`, body: { replace: false } })}
                          />
                        </>
                      ) : null}
                    </PipelineStep>

                    <PipelineStep
                      icon={Layers}
                      label="Storyboards"
                      status={combineStoryboardStatus(script)}
                      testid={`step-storyboards-${script.id}`}
                    >
                      <StoryboardsContent script={script} />
                      {failedSlots.map(({ slot, error }) => (
                        <div key={slot} className="space-y-1.5">
                          <p className="text-xs text-red-600" data-testid={`text-storyboard-error-${script.id}-${slot}`}>
                            Storyboard {slot}: {error || "generation failed."}
                          </p>
                          <RetryButton
                            label={`Retry storyboard ${slot}`}
                            testid={`button-retry-storyboard-${script.id}-${slot}`}
                            disabled={retryDisabled}
                            onClick={() => stepRetry.mutate({ path: `/api/video-express-scripts/${script.id}/storyboards/${slot}/regenerate` })}
                          />
                        </div>
                      ))}
                    </PipelineStep>

                    <PipelineStep
                      icon={Wand2}
                      label="Scene Prompts"
                      status={spStatus}
                      testid={`step-scene-prompts-${script.id}`}
                    >
                      <ScenePromptsContent script={script} />
                      {spStatus === "error" ? (
                        <>
                          <p className="text-xs text-red-600" data-testid={`text-scene-prompt-error-${script.id}`}>
                            {spError || "Scene prompt generation failed."}
                          </p>
                          <RetryButton
                            testid={`button-retry-scene-prompts-${script.id}`}
                            disabled={retryDisabled}
                            onClick={() => stepRetry.mutate({ path: `${campaignApi}/generate-scene-prompts` })}
                          />
                        </>
                      ) : null}
                    </PipelineStep>

                    {(() => {
                      if (!voStatus || voStatus === "idle") return null;
                      return (
                        <div data-testid={`voiceover-${script.id}`}>
                          <PipelineStep icon={Volume2} label="Voiceover" status={voStatus} testid={`step-voiceover-${script.id}`}>
                            <VoiceoverContent script={script} />
                            {voStatus === "failed" ? (
                              <RetryButton
                                testid={`button-retry-voiceover-${script.id}`}
                                disabled={retryDisabled}
                                onClick={() => stepRetry.mutate({ path: `/api/video-express-scripts/${script.id}/generate-voiceover` })}
                              />
                            ) : null}
                          </PipelineStep>
                        </div>
                      );
                    })()}

                    {(() => {
                      // Scene Clips — per-clip persisted errors + scoped re-run
                      // (the full-run route would reset and re-bill every scene).
                      const clips = deriveClipStepState(script);
                      if (clips.total === 0) return null;
                      return (
                        <PipelineStep icon={Clapperboard} label="Scene Clips" status={clips.status} testid={`step-clips-${script.id}`}>
                          <p className="text-xs text-muted-foreground" data-testid={`text-clips-summary-${script.id}`}>
                            {clips.readyCount} of {clips.total} clips rendered
                            {clips.pendingCount > 0 ? ` — ${clips.pendingCount} still generating` : ""}.
                          </p>
                          {clips.failures.map(({ sceneNumber, error, retryPath }) => (
                            <div key={sceneNumber} className="space-y-1.5">
                              <p className="text-xs text-red-600" data-testid={`text-clip-step-error-${script.id}-${sceneNumber}`}>
                                Clip {sceneNumber}: {error}
                              </p>
                              <RetryButton
                                label={`Re-run clip ${sceneNumber}`}
                                testid={`button-retry-clip-step-${script.id}-${sceneNumber}`}
                                disabled={retryDisabled}
                                onClick={() => stepRetry.mutate({ path: retryPath })}
                              />
                            </div>
                          ))}
                        </PipelineStep>
                      );
                    })()}

                    {(() => {
                      // Final Render — surfaces the render row's persisted error.
                      const render = rendersByScript?.[script.id];
                      const rs = deriveRenderStepState(script, render, allClipsReady(script));
                      if (!rs.visible) return null;
                      return (
                        <PipelineStep icon={Film} label="Final Render" status={rs.status} testid={`step-render-${script.id}`}>
                          {rs.error ? (
                            <>
                              <p className="text-xs text-red-600" data-testid={`text-render-step-error-${script.id}`}>{rs.error}</p>
                              <RetryButton
                                label="Retry final render"
                                testid={`button-retry-render-${script.id}`}
                                disabled={retryDisabled || !rs.canRetry}
                                onClick={() => stepRetry.mutate({ path: rs.retryPath })}
                              />
                            </>
                          ) : rs.status === "ready" ? (
                            <p className="text-xs text-muted-foreground">Final video rendered — playback and downloads are above.</p>
                          ) : rs.status === "generating" ? (
                            <p className="text-xs text-muted-foreground">Stitching the final video…</p>
                          ) : (
                            <p className="text-xs text-muted-foreground">All clips are ready — start the final render from the controls above.</p>
                          )}
                        </PipelineStep>
                      );
                    })()}

                    {(() => {
                      // Captions — a caption failure never blocks the video, but
                      // it must still be visible and retryable here.
                      const render = rendersByScript?.[script.id];
                      const cs = deriveCaptionStepState(render);
                      if (!cs.visible) return null;
                      return (
                        <PipelineStep icon={Captions} label="Captions" status={cs.status} testid={`step-captions-${script.id}`}>
                          {cs.error ? (
                            <>
                              <p className="text-xs text-red-600" data-testid={`text-captions-error-${script.id}`}>
                                {cs.error} The uncaptioned video is still available above.
                              </p>
                              <RetryButton
                                label="Retry captions"
                                testid={`button-retry-captions-${script.id}`}
                                disabled={retryDisabled}
                                onClick={() => stepRetry.mutate({ path: cs.retryPath })}
                              />
                            </>
                          ) : cs.status === "ready" ? (
                            <p className="text-xs text-muted-foreground">Captioned video ready.</p>
                          ) : (
                            <p className="text-xs text-muted-foreground">Adding captions…</p>
                          )}
                        </PipelineStep>
                      );
                    })()}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}
